'use client';

/**
 * API namespace `userFiles` — UserFile storage (SPEC-AI-CONTEXT-FILE-STORAGE §5.1).
 * ============================================================================
 * Wraps the 10 backend endpoints under `/api/user-files/*` mounted by PR1
 * (`backend/src/routes/user-file.routes.ts`). All endpoints require
 * `requireAuth` (the `apiFetch` helper attaches the Bearer access token).
 *
 * §6 Anti-Invención: every type + response shape mirrors the merged PR1 BE
 * contract in `backend/src/services/user-file.service.ts` verbatim
 * (`LeanUserFile`, `UploadWarning`, `StorageStatsResult`, `ListUserFilesResult`,
 * `USER_FILE_CATEGORIES`). No invented thresholds, no invented fields.
 *
 * Anti-P-013: 0 silent error swallow — callers in `use-storage-page.ts` wrap
 *             every call in try/catch + sonner toast + dev-only console.
 * Anti-P-041: 0 `as any`.
 * Anti-P-042: 0 `console.*` in this file (the namespace is pure plumbing;
 *             errors propagate via `ApiClientError` to the hook).
 * Anti-P-263: cero internal IDs leaked in UI strings.
 * Anti-P-347: UserFile shape, endpoints, soft cap, anti-enum 404 — all from
 *             SPEC §5 + PR1 merged code.
 * Anti-P-382: reuses `apiFetch` (JSON) + `apiFetchFormData` (multipart) —
 *             no parallel HTTP client.
 * Anti-P-383: this file ≤500L (split: types + namespace, no inline UI logic).
 *
 * Part of SPEC-AI-CONTEXT-FILE-STORAGE PR2 (FE storage page).
 * Spec ref: docs/SPEC-AI-CONTEXT-FILE-STORAGE.md §5.1, §7.1, §13 (PR2
 *           acceptance).
 */

import { apiFetch, apiFetchFormData } from '../core/fetch-client';
import { ApiClientError } from '../core/error-handler';

// ─── Types (mirror PR1 BE service types verbatim — anti-P-347) ──────────────

/** The 7 valid UserFile categories (SPEC §5.2 + `USER_FILE_CATEGORIES`). */
export type UserFileCategory =
  | 'avatar'
  | 'cover'
  | 'kyc'
  | 'market'
  | 'ai_context'
  | 'landing'
  | 'general';

/** UserFile lean shape (mirrors `LeanUserFile` in `user-file.service.ts`). */
export interface UserFile {
  fileId: string;
  userId: string;
  storagePath: string;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  folder: string;
  category: UserFileCategory;
  cdnUrl: string;
  lastAccessedAt: string;
  billingOverdue: boolean;
  billingOverdueAt: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * Soft cap warning — returned on upload when the new file puts the user
 * over the free tier (1 GB). Mirrors `UploadWarning` in the BE service.
 *
 * Anti-P-347: BE returns `overage: true` (literal boolean) — the FE check
 * uses `warning?.overage === true` (no truthy fallback).
 */
export interface SoftCapWarning {
  overage: true;
  overageBytes: number;
  billingDate: string; // YYYY-MM-01
}

/** Storage usage stats (mirrors `StorageStatsResult` in the BE service). */
export interface StorageStats {
  usedBytes: number;
  freeTierBytes: number;
  overageBytes: number;
  billingOverdue: boolean;
  lastBilledAt: string | null;
  costPerGbMonth: string; // string — preserves Decimal precision (0.1 CR/GB)
}

/** List endpoint response — `{ data, total, page, limit }` per SPEC §5.1. */
export interface UserFilesListResponse {
  data: UserFile[];
  total: number;
  page: number;
  limit: number;
}

/** Single-file GET response shape — `{ data: UserFile }`. */
export interface UserFileResponse {
  data: UserFile;
}

/** Upload endpoint response — `{ data: UserFile, warning?: SoftCapWarning }`. */
export interface UploadUserFileResponse {
  data: UserFile;
  warning?: SoftCapWarning | null;
}

/** Create-folder endpoint response — `{ data: { folder } }`. */
export interface CreateFolderResponse {
  data: { folder: string };
}

/** Categories endpoint response — `{ data: UserFileCategory[] }`. */
export interface ListCategoriesResponse {
  data: UserFileCategory[];
}

/** Storage-stats endpoint response — `{ data: StorageStats }`. */
export interface StorageStatsResponse {
  data: StorageStats;
}

/** Sort options supported by the list endpoint (SPEC §5.2 + BE `parseSort`). */
export type UserFileSortBy = 'createdAt' | 'sizeBytes' | 'fileName';
export type UserFileSortDir = 'asc' | 'desc';

// ─── Namespace: 8 client methods (PR1 mounted 10 BE endpoints; the 2 admin
//     trigger endpoints billing/run + cleanup/run are PR3-only + not used
//     by the FE storage page — left out of this namespace intentionally to
//     keep surface minimal until PR3 wires them). ───────────────────────────

export const userFiles = {
  /**
   * GET /api/user-files — list the caller's files. Backend returns
   * `{ data, total, page, limit }` (paginated). Filters: `folder`,
   * `category`, `search` (server-side). Sort: `sortBy` + `sortDir`.
   *
   * Anti-P-263: query params are URL-encoded via `URLSearchParams` (no
   *             manual concat — safer for folder paths with `/`).
   */
  async list(
    params: {
      folder?: string;
      category?: UserFileCategory;
      search?: string;
      page?: number;
      limit?: number;
      sortBy?: UserFileSortBy;
      sortDir?: UserFileSortDir;
      signal?: AbortSignal;
    } = {},
  ): Promise<UserFilesListResponse> {
    const qs = new URLSearchParams();
    if (params.folder) qs.set('folder', params.folder);
    if (params.category) qs.set('category', params.category);
    if (params.search) qs.set('search', params.search);
    if (params.page !== undefined) qs.set('page', String(params.page));
    if (params.limit !== undefined) qs.set('limit', String(params.limit));
    if (params.sortBy) qs.set('sortBy', params.sortBy);
    if (params.sortDir) qs.set('sortDir', params.sortDir);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    // rawResponse: true preserves the full envelope `{ data, total, page, limit }`
    // (apiFetch auto-unwraps `.data` by default — see fetch-client.ts L138-148).
    // The hook reads `result.data` + `result.total` (storage-page.tsx L137-138).
    return apiFetch<UserFilesListResponse>(`/user-files${suffix}`, {
      signal: params.signal,
      rawResponse: true,
    });
  },

  /**
   * GET /api/user-files/:fileId — fetch one (also bumps `lastAccessedAt`
   * on the BE for future LRU policy). Anti-enum: 404 `USER_FILE_NOT_FOUND`
   * for both "doesn't exist" AND "not yours" per SPEC §5.4 (R1 review fix).
   */
  async get(fileId: string, signal?: AbortSignal): Promise<UserFileResponse> {
    return apiFetch<UserFileResponse>(
      `/user-files/${encodeURIComponent(fileId)}`,
      { signal, rawResponse: true },
    );
  },

  /**
   * POST /api/user-files/upload — multipart upload. The browser sets the
   * multipart boundary automatically (we use `apiFetchFormData` which does
   * NOT set `Content-Type` — anti-P-382 + matches the
   * `api.market.orders.chat.uploadFile` pattern in `market-orders.ts`).
   *
   * On soft cap overage the BE sets `X-Storage-Overage: true` headers +
   * returns the `warning` field in the body — the FE surfaces this as a
   * sonner toast in the upload hook (see `use-storage-page.ts`).
   */
  async upload(
    params: {
      file: File;
      folder?: string;
      category?: UserFileCategory;
      fileName?: string;
      signal?: AbortSignal;
    },
  ): Promise<UploadUserFileResponse> {
    const formData = new FormData();
    formData.append('file', params.file);
    if (params.folder) formData.append('folder', params.folder);
    if (params.category) formData.append('category', params.category);
    if (params.fileName) formData.append('fileName', params.fileName);
    return apiFetchFormData<UploadUserFileResponse>('/user-files/upload', {
      method: 'POST',
      body: formData,
      signal: params.signal,
      rawResponse: true,
    });
  },

  /**
   * POST /api/user-files/folder — virtual folder creation (no DB record).
   * Validates the name + optional parent. Returns the combined path.
   */
  async createFolder(
    params: {
      folder: string;
      parent?: string;
      signal?: AbortSignal;
    },
  ): Promise<CreateFolderResponse> {
    return apiFetch<CreateFolderResponse>('/user-files/folder', {
      method: 'POST',
      body: JSON.stringify({ folder: params.folder, parent: params.parent }),
      signal: params.signal,
      rawResponse: true,
    });
  },

  /**
   * PATCH /api/user-files/:fileId — rename and/or move. Body: `{ fileName?,
   * folder? }`. At least one field must be present (BE throws
   * `USER_FILE_FOLDER_INVALID` otherwise — the FE hook validates this
   * client-side before calling).
   */
  async rename(
    fileId: string,
    params: {
      fileName?: string;
      folder?: string;
      signal?: AbortSignal;
    },
  ): Promise<UserFileResponse> {
    return apiFetch<UserFileResponse>(
      `/user-files/${encodeURIComponent(fileId)}`,
      {
        method: 'PATCH',
        body: JSON.stringify({
          ...(params.fileName !== undefined ? { fileName: params.fileName } : {}),
          ...(params.folder !== undefined ? { folder: params.folder } : {}),
        }),
        signal: params.signal,
        rawResponse: true,
      },
    );
  },

  /**
   * DELETE /api/user-files/:fileId — best-effort Bunny delete + DB
   * deleteOne. Returns `204 No Content` on success — the FE hook uses the
   * `ApiClientError.status === 204` absence-of-error as the success signal
   * (apiFetch resolves to an empty object for 204 per the handleResponse
   * unwrap).
   */
  async remove(fileId: string, signal?: AbortSignal): Promise<void> {
    await apiFetch<void>(`/user-files/${encodeURIComponent(fileId)}`, {
      method: 'DELETE',
      signal,
    });
  },

  /**
   * GET /api/user-files/storage — storage usage stats. Used by
   * `StorageBar` (top of the page) + the overdue banner.
   */
  async getStorageStats(signal?: AbortSignal): Promise<StorageStatsResponse> {
    return apiFetch<StorageStatsResponse>('/user-files/storage', {
      signal,
      rawResponse: true,
    });
  },

  /**
   * GET /api/user-files/categories — static list of the 7 categories.
   * Used by the upload zone + the category filter dropdown. The FE keeps a
   * local mirror as a fallback if the fetch fails (the list is static per
   * SPEC §5.2).
   */
  async listCategories(signal?: AbortSignal): Promise<ListCategoriesResponse> {
    return apiFetch<ListCategoriesResponse>('/user-files/categories', {
      signal,
      rawResponse: true,
    });
  },
};

/** Re-export ApiClientError so callers can `instanceof`-check error type. */
export { ApiClientError };
